import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Layers, Loader2, Eye, CheckCircle2 } from 'lucide-react';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminBulkPayoutsPage() {
  const { api } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/bulk-payouts/admin/all');
      setUploads(res.data?.uploads || []);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!detail) { setDetailData(null); return; }
    api.get(`/bulk-payouts/admin/${detail.id}`).then((r) => setDetailData(r.data)).catch(() => setDetailData(null));
  }, [detail, api]);

  const pendingCount = (detailData?.orders || []).filter((o) => o.status === 'pending_admin_review').length;

  const approveAll = async () => {
    if (!detail) return;
    setApproving(true);
    try {
      const res = await api.post(`/bulk-payouts/admin/${detail.id}/approve-all`);
      toast.success(res.data.message || 'Payouts approved');
      const r = await api.get(`/bulk-payouts/admin/${detail.id}`);
      setDetailData(r.data);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-bulk-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Layers className="w-6 h-6 text-primary" />Bulk Payouts</h1>
        <p className="text-sm text-muted-foreground mt-1">Batches of merchant payouts created via Excel/CSV upload. Approval follows the standard per-payout workflow.</p>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-lg font-heading">All Bulk Uploads</CardTitle></CardHeader>
        <CardContent>
          {uploads.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No bulk payouts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead><tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 px-3">Bulk ID</th><th className="py-2 px-3">Merchant</th><th className="py-2 px-3">Orders</th>
                  <th className="py-2 px-3">Amount</th><th className="py-2 px-3">Fees</th><th className="py-2 px-3">Date</th>
                  <th className="py-2 px-3">Status</th><th className="py-2 px-3"></th>
                </tr></thead>
                <tbody>
                  {uploads.map((u) => (
                    <tr key={u.id} className="border-b last:border-0 hover:bg-accent/40" data-testid={`admin-bulk-row-${u.bulk_id}`}>
                      <td className="py-2 px-3 font-mono">{u.bulk_id}</td>
                      <td className="py-2 px-3">{u.merchant?.business_name || u.merchant_id}</td>
                      <td className="py-2 px-3">{u.valid_rows}</td>
                      <td className="py-2 px-3 font-mono">{fmtINR(u.total_amount)}</td>
                      <td className="py-2 px-3 font-mono">{fmtINR((u.total_fees || 0) + (u.total_tax || 0))}</td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '—'}</td>
                      <td className="py-2 px-3"><Badge variant="secondary">{u.status}</Badge></td>
                      <td className="py-2 px-3"><Button size="sm" variant="ghost" onClick={() => setDetail(u)} data-testid={`admin-bulk-view-${u.bulk_id}`}><Eye className="w-4 h-4" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-bulk-detail">
          <DialogHeader><DialogTitle>Bulk {detail?.bulk_id} — {detail?.merchant?.business_name || ''}</DialogTitle>
            <DialogDescription>All individual payout orders in this batch. Approval uses the standard payout workflow.</DialogDescription>
          </DialogHeader>
          {detailData && pendingCount > 0 && (
            <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 p-3">
              <p className="text-sm">{pendingCount} order(s) pending approval</p>
              <Button size="sm" onClick={approveAll} disabled={approving} data-testid="admin-approve-all-btn">
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Approve All Pending
              </Button>
            </div>
          )}
          {detailData ? (
            <div className="rounded-lg border overflow-x-auto max-h-96">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="border-b text-left text-muted-foreground bg-muted/30">
                  <th className="py-2 px-3">Order ID</th><th className="py-2 px-3">Beneficiary</th><th className="py-2 px-3">Amount</th>
                  <th className="py-2 px-3">Total Debit</th><th className="py-2 px-3">Status</th>
                </tr></thead>
                <tbody>
                  {(detailData.orders || []).map((o) => (
                    <tr key={o.id} className="border-b last:border-0">
                      <td className="py-2 px-3 font-mono">{o.order_id}</td>
                      <td className="py-2 px-3">{o.beneficiary_name}</td>
                      <td className="py-2 px-3 font-mono">{fmtINR(o.amount)}</td>
                      <td className="py-2 px-3 font-mono">{fmtINR(o.total_debit)}</td>
                      <td className="py-2 px-3"><Badge variant="secondary">{o.status?.replace(/_/g, ' ')}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
